import { ChangeEvent, useCallback, useEffect } from "react";
import { FieldErrors, useForm } from "react-hook-form";

import { useTranslation } from "next-i18next";

import { isValidIBAN } from "ibantools";

import useCreateOrUpdateBankAccount from "@/Apollo/hooks/useCreateOrUpdateBankAccount";
import { AccountPaymentInformationFormData } from "@/common/contracts/account.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import {
  getBankAccountNumberInElectronicFormat,
  getBankAccountNumberInPrintFormat,
  maskBankAccountNumber,
} from "@/common/utils/payment.utils";
import { useAccountSettingsPaymentInformationSMSGuard } from "@/components/Account/hooks/useAccountSettingsPaymentInformationSMSGuard";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import addToast from "@/toast/addToast";
import { Button } from "@/ui/cva/Button";
import { Input } from "@/ui/cva/input/Input";
import { InputGroup } from "@/ui/cva/input/InputGroup";
import { InputGroupErrorHelper } from "@/ui/cva/input/InputGroupErrorHelper";
import { InputLabel } from "@/ui/typography/InputLabel";

export type BankAccountFormAccount = {
  country: string;
  last4: string;
  account_holder_name?: string | null;
};

type BankAccountFormProps = {
  bankAccount: BankAccountFormAccount | null;
  /** Errored account: keep the holder name, make the seller retype the IBAN. */
  clearNumber?: boolean;
  /** Solid save button: this form is the page's one primary action. */
  primary?: boolean;
  onSaved: () => Promise<unknown> | void;
  onCancel?: () => void;
};

/**
 * The IBAN form, moved verbatim from the old page: the SMS wall guards the
 * change (07.6), the masked number stays untouched unless retyped, and form
 * errors go to analytics. It renders inside the bank account task or under
 * the bank account row, never on its own at the top of the page.
 */
export const BankAccountForm = ({
  bankAccount,
  clearNumber = false,
  primary = false,
  onSaved,
  onCancel,
}: BankAccountFormProps) => {
  const { t } = useTranslation();
  const { copy } = usePaymentCopy();
  const { isEditable, otpCode, smsGuard, smsGuardReset } =
    useAccountSettingsPaymentInformationSMSGuard();
  const [createOrUpdateBankAccount, { loading: saving }] =
    useCreateOrUpdateBankAccount();

  const {
    register,
    setValue,
    getValues,
    handleSubmit: formHandleSubmit,
    formState: { errors },
  } = useForm<AccountPaymentInformationFormData>();

  const initializeInputs = useCallback(() => {
    if (!bankAccount) {
      return;
    }

    setValue("bankAccountHolderName", bankAccount.account_holder_name ?? "");

    if (clearNumber) {
      setValue("bankAccountNumber", "");

      return;
    }

    const masked = maskBankAccountNumber(
      bankAccount.country,
      bankAccount.last4
    );

    if (masked) {
      setValue("bankAccountNumber", getBankAccountNumberInPrintFormat(masked));
    }
  }, [bankAccount, clearNumber, setValue]);

  useEffect(() => {
    initializeInputs();
  }, [initializeInputs]);

  const handleSubmit = async (data: AccountPaymentInformationFormData) => {
    try {
      await createOrUpdateBankAccount({
        variables: {
          input: {
            account_number: !data.bankAccountNumber.includes("*")
              ? getBankAccountNumberInElectronicFormat(data.bankAccountNumber)
              : undefined,
            account_holder_name: data.bankAccountHolderName,
            otp: otpCode ?? undefined,
          },
        },
      });

      smsGuardReset();
      await onSaved();

      addToast(t("account:your-account-settings-saved-payment-information"), {
        appearance: "success",
      });
    } catch (error) {
      console.error("BankAccountForm", (error as Error).message);
      addToast(
        t("account:your-account-settings-error-saving-payment-information"),
        { appearance: "error" }
      );
    }
  };

  const handleBankNumberInput = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(
      "bankAccountNumber",
      getBankAccountNumberInPrintFormat(event.target.value)
    );
  };

  const handleError = (fieldErrors: FieldErrors) => {
    analyticsService.trackAllFormErrors(
      "account_payment_information",
      fieldErrors
    );
  };

  const normalizeValues = () => {
    const holderName = getValues("bankAccountHolderName");
    const number = getValues("bankAccountNumber");

    if (holderName) {
      setValue("bankAccountHolderName", holderName.trim());
    }

    if (number) {
      setValue("bankAccountNumber", number.trim().toUpperCase());
    }
  };

  const appearance = primary ? "solid" : "outlined";

  return (
    <form
      onSubmit={formHandleSubmit(handleSubmit, handleError)}
      className="flex flex-col gap-6 md:max-w-md"
      data-testid="bank-account-form"
    >
      <InputGroup
        valid={!errors["bankAccountHolderName"]}
        disabled={!isEditable}
        label={props => (
          <InputLabel className="text-base" {...props}>
            {t("account:your-account-settings-bank-holder-name")}
          </InputLabel>
        )}
        input={props => (
          <Input
            placeholder={t(
              "account:your-account-settings-bank-holder-name-placeholder"
            )}
            {...register("bankAccountHolderName", { required: true })}
            {...props}
          />
        )}
        helper={props => (
          <InputGroupErrorHelper
            {...props}
            error={errors["bankAccountHolderName"]}
          />
        )}
      />
      <InputGroup
        valid={!errors["bankAccountNumber"]}
        disabled={!isEditable}
        label={props => (
          <InputLabel className="text-base" {...props}>
            {t("account:your-account-settings-bank-account-number")}
          </InputLabel>
        )}
        input={props => (
          <Input
            onInput={handleBankNumberInput}
            placeholder={t(
              "account:your-account-settings-bank-account-number-placeholder"
            )}
            {...register("bankAccountNumber", {
              required: t("account:your-account-settings-validation-required"),
              validate: value => {
                const electronic =
                  getBankAccountNumberInElectronicFormat(value);

                if (!electronic.includes("*") && !isValidIBAN(electronic)) {
                  return t(
                    "account:your-account-settings-validation-invalid-iban"
                  );
                }
              },
            })}
            {...props}
          />
        )}
        helper={props => (
          <InputGroupErrorHelper
            {...props}
            error={errors["bankAccountNumber"]}
          />
        )}
      />
      <div className="flex flex-wrap gap-3">
        {isEditable ? (
          <Button
            size="large"
            appearance={appearance}
            loading={saving}
            onClick={normalizeValues}
          >
            {copy("bankSave")}
          </Button>
        ) : (
          <Button
            type="button"
            size="large"
            appearance={appearance}
            onClick={() => void smsGuard(bankAccount?.last4)}
          >
            {bankAccount ? copy("bankChange") : copy("bankAdd")}
          </Button>
        )}
        {onCancel && (
          <Button
            type="button"
            size="large"
            appearance="minimal"
            onClick={() => {
              smsGuardReset();
              onCancel();
            }}
          >
            {copy("bankCancel")}
          </Button>
        )}
      </div>
    </form>
  );
};
